/**
 * Shelf (rules v2, "Side shelf", Lagos L1661): a wooden shelf in the strip under the board (where
 * the conveyor sits; a level never has both) holding its row of items left to right, every one in
 * view so the player can plan. The two ends, the first and the last item still on the row, stand
 * lifted off the plank and lit, a mustard glow under each: they are the only tap targets. The
 * items between them rest on the plank dimmed like covered tiles (60% brightness: still readable).
 * A tap on an inner item is refused with the usual "nope" wobble. A taken end flies to the bag like
 * any tile (its TileView is detached), and its neighbour lifts and lights up as the new end (core's
 * `shelved`; the row never slides: each item keeps its place, a taken one leaves a gap). An undo
 * slides the item back from the bag to its own place, an end again, and the end it replaces
 * settles back down. Code-drawn like the tray and the machine.
 */
import type * as Phaser from 'phaser';
import { PIECE_BOARD, type CompiledLevel, type Round } from '@mp/core';
import type { Rect } from '../game/layout.js';
import { C } from '../game/theme.js';
import { vp } from '../game/viewport.js';
import { deco, dur } from '../fx/motion.js';
import { TileView } from './board.js';
import { NO_LOOKS, type ItemLooks } from './looks.js';
import { isShelfEnd, placeAt, placeCentre, shelfGeometry, shelfOf, shelfPieces, shelfSlot, type ShelfGeometry } from './shelves.js';

/** PRD §6: every tap target is at least 44 CSS px. */
const MIN_TARGET = 44;

export class ShelfView {
  /** the row's TileViews by piece (items on the plank; a taken one is the bag's until an undo) */
  private readonly views = new Map<number, TileView>();
  /** lockKey: does piece i wear the key badge (the board's rule, so both hold it until the key pops)? */
  keyOf: ((r: Round, i: number) => boolean) | null = null;
  private readonly geo: ShelfGeometry[] = [];
  private readonly glow: Phaser.GameObjects.Graphics;
  /** the places the glow marks now, per shelf (redrawn when the ends change) */
  private glowAt = '';
  /** the win sweep took the row's items: a late sync (a key landing after the last pop) draws none again */
  private swept = false;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly L: CompiledLevel,
    area: Rect,
    round: Round,
    /** Clearer items: the art each item is drawn with (looks.ts); every item as itself by default */
    private readonly looks: ItemLooks = NO_LOOKS,
  ) {
    const u = vp.u;
    // one shelf per level today (MAX_SHELVES); more would share the strip as equal rows
    const rows = Math.max(1, L.nShelves);
    const plank = scene.add.graphics().setDepth(40);
    for (let s = 0; s < L.nShelves; s++) {
      const r = { x: area.x, y: area.y + (area.h / rows) * s, w: area.w, h: area.h / rows };
      const g = shelfGeometry(r, shelfPieces(L, s).length, u);
      this.geo.push(g);
      this.drawPlank(plank, g);
    }
    this.glow = scene.add.graphics().setDepth(41);
    this.sync(round, false);
  }

  /** The plank: a kraft board with a darker front lip and a shadow, on two brackets at its ends. */
  private drawPlank(g: Phaser.GameObjects.Graphics, geo: ShelfGeometry): void {
    const u = vp.u;
    const p = geo.plank;
    const lip = p.h * 0.55;
    const r = Math.min(6 * u, p.h / 2);
    // brackets under the plank, near each end
    g.fillStyle(C.kraftDark, 1);
    g.lineStyle(2.5 * u, C.ink, 1);
    for (const x of [p.x + p.w * 0.12, p.x + p.w * 0.88]) {
      const bw = 9 * u;
      const bh = geo.size * 0.34;
      g.fillTriangle(x - bw / 2, p.y + p.h, x + bw / 2, p.y + p.h, x - bw / 2, p.y + p.h + bh);
      g.strokeTriangle(x - bw / 2, p.y + p.h, x + bw / 2, p.y + p.h, x - bw / 2, p.y + p.h + bh);
    }
    // shadow, top face, front lip
    g.fillStyle(C.ink, 0.22);
    g.fillRoundedRect(p.x, p.y + 4 * u, p.w, p.h + lip, r);
    g.fillStyle(C.kraft, 1);
    g.fillRoundedRect(p.x, p.y, p.w, p.h + lip, r);
    g.fillStyle(C.kraftDark, 1);
    g.fillRect(p.x + r, p.y + p.h, p.w - 2 * r, lip * 0.7);
    g.lineStyle(2.5 * u, C.ink, 1);
    g.strokeRoundedRect(p.x, p.y, p.w, p.h + lip, r);
    // the wood's grain: two light lines along the top face
    g.lineStyle(1.5 * u, 0xffffff, 0.45);
    g.lineBetween(p.x + 10 * u, p.y + p.h * 0.35, p.x + p.w * 0.45, p.y + p.h * 0.35);
    g.lineBetween(p.x + p.w * 0.55, p.y + p.h * 0.6, p.x + p.w - 10 * u, p.y + p.h * 0.6);
  }

  private geoOf(i: number): ShelfGeometry {
    return this.geo[shelfOf(this.L, i)]!;
  }

  /** Centre of piece i on its row now (an end: lifted). */
  private at(r: Round, i: number): { x: number; y: number } {
    return placeCentre(this.geoOf(i), shelfSlot(this.L, i), isShelfEnd(r, i));
  }

  /**
   * Follow core state: every item still on a row drawn at its place (a new one only at the first
   * build), the ends lifted and lit, the inner items resting dimmed; the key badge by the board's
   * rule; an assist swap's relabel. `animate` tweens the changes (a new end rising, an old one
   * settling after an undo).
   */
  sync(r: Round, animate: boolean): void {
    if (this.swept) return;
    const d = animate ? dur(200) : 0;
    for (let s = 0; s < this.L.nShelves; s++) {
      const g = this.geo[s]!;
      for (const i of shelfPieces(this.L, s)) {
        let v = this.views.get(i);
        if (r.piece[i] !== PIECE_BOARD) {
          // taken without a flight (a view core no longer has on the row): gone
          if (v) {
            this.views.delete(i);
            v.destroy();
          }
          continue;
        }
        const end = isShelfEnd(r, i);
        const p = this.at(r, i);
        if (!v) {
          v = new TileView(this.scene, i, false, g.size);
          v.setPosition(p.x, p.y).setDepth(50);
          this.views.set(i, v);
        }
        if (v.getData('item') !== r.item[i]) {
          v.setItem(this.looks.of(this.L.items[r.item[i]!]!));
          v.setData('item', r.item[i]);
        }
        v.setCovered(!end, animate);
        v.setKey(this.keyOf?.(r, i) ?? false, animate);
        // an item flying back from the bag stays over the bag until it lands (attach), then syncs
        if (v.getData('flying')) continue;
        // an end stands a little forward of its neighbours
        v.setDepth(end ? 52 : 50);
        // (a view back from the bag may have been made at another size: a rebuilt bag's are tile-sized)
        const sc = g.size / v.size;
        if (d && (Math.abs(v.x - p.x) > 0.5 || Math.abs(v.y - p.y) > 0.5)) {
          this.scene.tweens.killTweensOf(v);
          this.scene.tweens.add({ targets: v, x: p.x, y: p.y, scale: sc, duration: d, ease: end ? 'Back.easeOut' : 'Cubic.easeOut' });
        } else if (!d) v.setPosition(p.x, p.y).setScale(sc);
      }
    }
    this.drawGlow(r, animate);
  }

  /** A soft mustard glow on the plank under each end: the places a tap takes. */
  private drawGlow(r: Round, animate: boolean): void {
    const ends: { s: number; g: ShelfGeometry; k: number }[] = [];
    for (let s = 0; s < this.L.nShelves; s++)
      for (const i of shelfPieces(this.L, s)) if (isShelfEnd(r, i)) ends.push({ s, g: this.geo[s]!, k: shelfSlot(this.L, i) });
    const key = ends.map((e) => `${e.s}:${e.k}`).join(',');
    if (key === this.glowAt) return;
    this.glowAt = key;
    const u = vp.u;
    const gl = this.glow;
    gl.clear();
    for (const { g, k } of ends) {
      const c = placeCentre(g, k, false);
      const w = g.size * 0.98;
      const h = Math.max(6 * u, g.plank.h * 0.9);
      gl.fillStyle(C.mustard, 0.85);
      gl.fillRoundedRect(c.x - w / 2, g.plank.y - h * 0.15, w, h, h / 2);
      gl.lineStyle(2 * u, C.mustardDark, 1);
      gl.strokeRoundedRect(c.x - w / 2, g.plank.y - h * 0.15, w, h, h / 2);
    }
    const d = animate ? deco(180) : 0;
    this.scene.tweens.killTweensOf(gl);
    if (!d) return void gl.setAlpha(1);
    gl.setAlpha(0.2);
    this.scene.tweens.add({ targets: gl, alpha: 1, duration: d, ease: 'Sine.easeOut' });
  }

  /** The row item under a device-px point (an end, or an inner one for the "nope"), or -1. */
  hitTest(x: number, y: number, r: Round): number {
    for (let s = 0; s < this.L.nShelves; s++) {
      const pieces = shelfPieces(this.L, s);
      const ends = pieces.filter((i) => isShelfEnd(r, i)).map((i) => shelfSlot(this.L, i));
      const k = placeAt(this.geo[s]!, pieces.length, x, y, ends, MIN_TARGET * vp.u);
      const i = k >= 0 ? pieces[k]! : -1;
      if (i >= 0 && r.piece[i] === PIECE_BOARD && this.views.has(i)) return i;
    }
    return -1;
  }

  /** Centre of piece i on its row (null when it is not on it). */
  pos(i: number, r: Round): { x: number; y: number } | null {
    return r.piece[i] === PIECE_BOARD ? this.at(r, i) : null;
  }

  /** Is piece i drawn on its row (test hooks)? */
  drawn(i: number): boolean {
    return !!this.views.get(i)?.visible;
  }

  /** Take an item's view off the row (it flies to the bag, or to the ticket under the magnet). */
  detach(i: number): TileView | null {
    const v = this.views.get(i) ?? null;
    this.views.delete(i);
    if (v) {
      this.scene.tweens.killTweensOf(v);
      v.setData('flying', false);
      v.setAngle(0).setAlpha(1);
      v.lightUp();
      // lockKey: the badge marks key pieces on the shelf only (a returning one gets it back in sync)
      v.setKey(false, false);
    }
    return v;
  }

  /**
   * An undo (or a rescue) slides an item back from the bag to its own place on the row, over the
   * bag while it flies (sync leaves it be); landed, the row syncs: its depth, and its place when
   * another item came back meanwhile and it is no longer an end.
   */
  attach(i: number, v: TileView, r: Round, animate: boolean): void {
    this.views.set(i, v);
    const g = this.geoOf(i);
    const p = this.at(r, i);
    const scale = g.size / v.size;
    const d = animate ? dur(220) : 0;
    this.scene.tweens.killTweensOf(v);
    v.setAlpha(1).setAngle(0);
    if (!d) {
      v.setPosition(p.x, p.y).setScale(scale).setDepth(isShelfEnd(r, i) ? 52 : 50);
      return;
    }
    v.setDepth(6000);
    v.setData('flying', true);
    this.scene.tweens.add({
      targets: v,
      x: p.x,
      y: p.y,
      scale,
      duration: d,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        v.setData('flying', false);
        if (this.views.get(i) === v) this.sync(r, true);
      },
    });
  }

  /** Soft "nope" for a tap on an inner item: it wobbles where it rests (GP-01.1; not while it flies back from the bag). */
  nope(i: number): void {
    const v = this.views.get(i);
    if (v && !v.getData('flying')) v.nope();
  }

  /** Decorative: after the intro the two ends hop once, pointing out what can be taken. */
  hopEnds(r: Round): void {
    const d = deco(260);
    if (!d) return;
    let k = 0;
    for (const [i, v] of this.views) {
      if (!isShelfEnd(r, i)) continue;
      const y = v.y;
      this.scene.tweens.add({ targets: v, y: y - this.geoOf(i).size * 0.18, duration: d / 2, delay: k++ * 90, yoyo: true, ease: 'Sine.easeOut', onComplete: () => v.setY(y) });
    }
  }

  /** Key badges on the row (test hooks). */
  keysShown(): number {
    let n = 0;
    for (const v of this.views.values()) if (v.visible && v.keyBadge) n++;
    return n;
  }

  /** Items drawn on the rows (test hooks). */
  get shown(): number {
    let n = 0;
    for (const v of this.views.values()) if (v.visible) n++;
    return n;
  }

  /** Win sweep: the items left on the row fly off with the board's leftovers (their views, taken off the row). */
  remaining(): TileView[] {
    const out = [...this.views.values()];
    this.views.clear();
    this.swept = true;
    this.glow.clear();
    this.glowAt = '';
    return out;
  }
}
